import { Microphone, MicrophoneSlash, Stop } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";

import { Button } from "./button";
import { Tooltip } from "./tooltip";

type AlibabaVoiceInputProps = {
  onResult: (text: string) => void;
  onError?: (error: string) => void;
  className?: string;
  disabled?: boolean;
  sampleRate?: number;
};
// 录音状态
enum RecordingState {
  IDLE = 0,
  RECORDING = 1,
  PROCESSING = 2,
}

export const AlibabaVoiceInput = forwardRef<HTMLButtonElement, AlibabaVoiceInputProps>(
  (
    {
      onResult,
      onError,
      className,
      disabled = false,
      sampleRate = 16000,
    },
    ref,
  ) => {
    const [recordingState, setRecordingState] = useState(RecordingState.IDLE);
    const [isSupported, setIsSupported] = useState(false);
    
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const streamRef = useRef<MediaStream | null>(null);

    // 检查浏览器支持
    useEffect(() => {
      const checkSupport = () => {
        const hasMediaRecorder = typeof MediaRecorder !== "undefined";
        const hasGetUserMedia = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
        
        setIsSupported(hasMediaRecorder && hasGetUserMedia);
      };

      checkSupport();
    }, []);

    // 将音频转换为Base64
    const audioToBase64 = useCallback((audioBlob: Blob): Promise<{base64: string, originalSize: number}> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // 移除data URL前缀，只保留base64数据
          const base64String = result.split(',')[1];
          
          // 验证Base64数据的有效性
          if (!base64String || base64String.length === 0) {
            reject(new Error("音频数据为空"));
            return;
          }
          
          console.log("音频Base64编码长度:", base64String.length);
          console.log("音频原始文件大小:", audioBlob.size);
          resolve({
            base64: base64String,
            originalSize: audioBlob.size
          });
        };
        reader.onerror = () => {
          reject(new Error("音频文件读取失败"));
        };
        reader.readAsDataURL(audioBlob);
      });
    }, []);

    // 调用阿里云录音文件识别（极速版）API
    const recognizeSpeech = useCallback(async (audioData: {base64: string, originalSize: number}, format: string = "wav") => {
      try {
        setRecordingState(RecordingState.PROCESSING);

        // 调用后端代理API
        const response = await fetch('/api/speech/recognize', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            speech: audioData.base64,
            format: format,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        
        console.log("语音识别API响应:", result);
        
        if (result.success && result.text) {
          onResult(result.text);
        } else {
          throw new Error(result.message || '语音识别失败');
        }

        setRecordingState(RecordingState.IDLE);

      } catch (error) {
        console.error("语音识别失败:", error);
        onError?.("语音识别失败: " + (error instanceof Error ? error.message : String(error)));
        setRecordingState(RecordingState.IDLE);
      }
    }, [onResult, onError]);

    // 开始录音
    const startRecording = useCallback(async () => {
      try {
        // 获取麦克风权限
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            sampleRate: sampleRate,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
          } 
        });
        
        streamRef.current = stream;
        audioChunksRef.current = [];

        // 创建MediaRecorder - 优先使用阿里云支持的格式
        let mediaRecorder: MediaRecorder;
        
        // 阿里云支持的格式优先级：WAV > MP3 > WebM(转换为PCM)
        const supportedTypes = [
          "audio/wav",
          "audio/mp4", // MP3 container
          "audio/webm;codecs=opus",
          "audio/webm"
        ];
        
        let selectedMimeType = "";
        for (const mimeType of supportedTypes) {
          if (MediaRecorder.isTypeSupported(mimeType)) {
            selectedMimeType = mimeType;
            break;
          }
        }
        
        if (!selectedMimeType) {
          throw new Error("浏览器不支持音频录制");
        }
        
        console.log("选择的音频格式:", selectedMimeType);
        
        mediaRecorder = new MediaRecorder(stream, {
          mimeType: selectedMimeType,
        });
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = async () => {
          // 使用实际的MIME类型创建Blob
          const audioBlob = new Blob(audioChunksRef.current, { type: selectedMimeType });
          const audioData = await audioToBase64(audioBlob);
          
          // 根据实际格式设置API参数 - 对于webm格式，告诉后端需要转换
          let format = "wav"; // 默认使用wav
          if (selectedMimeType.includes("wav")) {
            format = "wav";
          } else if (selectedMimeType.includes("mp4")) {
            format = "mp3";
          } else if (selectedMimeType.includes("webm")) {
            // WebM格式需要后端处理，但先尝试作为PCM发送
            format = "pcm";
          }
          
          console.log("发送给后端的格式:", format);
          console.log("实际音频格式:", selectedMimeType);
          
          await recognizeSpeech(audioData, format);
        };

        mediaRecorder.start();
        setRecordingState(RecordingState.RECORDING);

      } catch (error) {
        console.error("开始录音失败:", error);
        onError?.("无法访问麦克风");
      }
    }, [sampleRate, audioToBase64, recognizeSpeech, onError]);

    // 停止录音
    const stopRecording = useCallback(() => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    }, []);

    // 切换录音状态
    const toggleRecording = useCallback(() => {
      if (recordingState === RecordingState.RECORDING) {
        stopRecording();
      } else if (recordingState === RecordingState.IDLE) {
        startRecording();
      }
    }, [recordingState, startRecording, stopRecording]);

    // 清理资源
    useEffect(() => {
      return () => {
        stopRecording();
      };
    }, [stopRecording]);

    // 检查配置是否完整
    const isConfigured = true; // 后端代理处理配置检查

    if (!isSupported) {
      return (
        <Tooltip content="您的浏览器不支持语音输入功能">
          <Button
            ref={ref}
            type="button"
            size="sm"
            variant="ghost"
            className={cn("px-2 opacity-50", className)}
            disabled={true}
          >
            <MicrophoneSlash />
          </Button>
        </Tooltip>
      );
    }

    if (!isConfigured) {
      return (
        <Tooltip content="语音识别服务未配置">
          <Button
            ref={ref}
            type="button"
            size="sm"
            variant="ghost"
            className={cn("px-2 opacity-50", className)}
            disabled={true}
          >
            <MicrophoneSlash />
          </Button>
        </Tooltip>
      );
    }

    const isRecording = recordingState === RecordingState.RECORDING;
    const isProcessing = recordingState === RecordingState.PROCESSING;

    return (
      <Tooltip content={
        isProcessing ? "正在识别..." : 
        isRecording ? "停止语音输入" : 
        "开始语音输入"
      }>
        <Button
          ref={ref}
          type="button"
          size="sm"
          variant={isRecording ? "error" : "ghost"}
          className={cn("px-2", className)}
          disabled={disabled}
          onClick={toggleRecording}
        >
          {isRecording ? <Stop /> : <Microphone />}
        </Button>
      </Tooltip>
    );
  },
);

AlibabaVoiceInput.displayName = "AlibabaVoiceInput";

// 类型声明
declare global {
  interface Window {
    AudioContext: typeof AudioContext;
    webkitAudioContext: typeof AudioContext;
  }
}